/**
 * Utilities for handling session tokens in placement test mode.
 *
 * The session token is a simple identifier generated by the frontend onboarding app.
 * Configuration data is sent back to the parent window via postMessage.
 */

export interface PlacementTestConfig {
  sessionToken: string; // Simple session identifier from parent window
  merchantId: string; // Merchant/team ID for context
  coveragePrice?: string; // Optional coverage price to display in widget
}

/**
 * Parses the URL parameter to extract placement test configuration.
 * Format: ?redo_placement_test={sessionToken}&merchant_id={merchantId}&coverage_price={price}
 */
export function parseUrlParams(): PlacementTestConfig | null {
  const urlParams = new URLSearchParams(window.location.search);
  const sessionToken = urlParams.get("redo_placement_test");
  const merchantId = urlParams.get("merchant_id");
  const coveragePrice = urlParams.get("coverage_price");

  if (!sessionToken || !merchantId) {
    return null;
  }

  return {
    sessionToken,
    merchantId,
    ...(coveragePrice && { coveragePrice }),
  };
}

/**
 * Checks sessionStorage and URL parameters for placement test mode configuration.
 * Returns the parsed config if valid, null otherwise.
 *
 * Checks sessionStorage first (persists across navigation), then falls back to URL parameter.
 */
export function checkForPlacementTestMode(): PlacementTestConfig | null {
  // Check sessionStorage first - config persists across page navigation
  const storedConfig = sessionStorage.getItem("redo_placement_test_config");

  if (storedConfig) {
    try {
      return JSON.parse(storedConfig);
    } catch (error) {
      console.error("Failed to parse stored placement test config:", error);
      sessionStorage.removeItem("redo_placement_test_config");
    }
  }

  // If not in sessionStorage, parse from URL (first-time initialization)
  const config = parseUrlParams();

  if (config) {
    // Store config in session for persistence across page navigation
    sessionStorage.setItem(
      "redo_placement_test_config",
      JSON.stringify(config),
    );
    console.log(
      "Entering placement test mode for merchant:",
      config.merchantId,
    );
  }

  return config;
}

/**
 * Gets the stored session token from session storage.
 * Used when sending data back to parent window via postMessage.
 */
export function getStoredTestToken(): string | null {
  const storedConfig = sessionStorage.getItem("redo_placement_test_config");
  if (!storedConfig) {
    return null;
  }

  try {
    const config = JSON.parse(storedConfig);
    return config.sessionToken;
  } catch (error) {
    console.error("Failed to parse stored config:", error);
    return null;
  }
}

/**
 * Clears the session token and configurator state from session storage.
 * Called after successful placement capture or on error.
 */
export function clearTestToken(): void {
  sessionStorage.removeItem("redo_placement_test_config");
  sessionStorage.removeItem("redo_placement_configurator_state");
}
